import React from 'react';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Building, MapPin, Edit, Trash2, ExternalLink } from 'lucide-react';
import { EmailPrivacyField, PhonePrivacyField } from '../components/EmployeePrivacyField';

export function getStatusLabel(status, t) {
  switch (status) {
    case 'ACTIVE':   return t('employeesPage.status.active');
    case 'INACTIVE': return t('employeesPage.status.inactive');
    case 'SUSPENDED':return t('employeesPage.status.suspended');
    case 'ARCHIVED': return t('employeesPage.status.archived');
    default:         return status || t('common.none');
  }
}

export function renderCellContent(employee, columnId, {
  selectedEmployees, setSelectedEmployees, setShowBulkActions,
  handleEdit, setSelectedEmployee, setShowDeleteDialog, t, language,
}) {
  const formatDate = (value) => new Date(value).toLocaleDateString(language);
  switch (columnId) {
    case 'select':
      return (
        <Checkbox
          checked={selectedEmployees.includes(employee.id)}
          onCheckedChange={(checked) => {
            if (checked) {
              setSelectedEmployees([...selectedEmployees, employee.id]);
              setShowBulkActions(true);
            } else {
              const next = selectedEmployees.filter((id) => id !== employee.id);
              setSelectedEmployees(next);
              if (next.length === 0) setShowBulkActions(false);
            }
          }}
        />
      );

    case 'registration_number':
      return <span className="font-medium">{employee.registration_number || '—'}</span>;

    case 'last_name':
      return <span className="font-medium">{employee.last_name || '—'}</span>;

    case 'first_name':
      return <span>{employee.first_name || '—'}</span>;

    case 'email':
      return employee.email
        ? <EmailPrivacyField email={employee.email} className="text-sm" showToggle={false} />
        : <span className="text-muted-foreground">—</span>;

    case 'phone_number':
      return employee.phone_number
        ? <PhonePrivacyField phone={employee.phone_number} className="text-sm" showToggle={false} />
        : <span className="text-muted-foreground">—</span>;

    case 'birth_date':
      return employee.birth_date
        ? <span className="text-sm">{formatDate(employee.birth_date)}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'job_name': {
      const positionLabel = employee.job_title || employee.job_name;
      return positionLabel
        ? <Badge variant="outline">{positionLabel}</Badge>
        : <span className="text-muted-foreground">—</span>;
    }

    case 'department_name':
      return (employee.department_name || employee.department_id)
        ? <div className="flex items-center space-x-1 text-sm"><Building className="h-3 w-3" /><span>{employee.department_name || `Dept ${employee.department_id}`}</span></div>
        : <span className="text-muted-foreground">—</span>;

    case 'site_name':
      return (employee.site_name || employee.site_id)
        ? <div className="flex items-center space-x-1 text-sm"><MapPin className="h-3 w-3" /><span>{employee.site_name || `Site ${employee.site_id}`}</span></div>
        : <span className="text-muted-foreground">—</span>;

    case 'status': {
      const label = getStatusLabel(employee.status, t);
      const baseVariant = employee.status === 'ACTIVE' ? 'default' : 'secondary';
      const extraClass = employee.status === 'SUSPENDED'
        ? 'bg-amber-950/30 text-amber-400 border-amber-900/40'
        : employee.status === 'ARCHIVED' ? 'bg-muted text-muted-foreground' : undefined;
      return <Badge variant={baseVariant} className={extraClass}>{label}</Badge>;
    }

    case 'contract_type':
      return employee.contract_type
        ? <Badge variant="outline">{employee.contract_type}</Badge>
        : <span className="text-muted-foreground">—</span>;

    case 'role':
      return employee.role
        ? <Badge variant="secondary">{employee.role}</Badge>
        : <span className="text-muted-foreground">—</span>;

    case 'badge_number':
      return employee.badge_number
        ? <Badge variant="outline" className="text-xs">{employee.badge_number}</Badge>
        : <span className="text-muted-foreground">—</span>;

    case 'fingerprint_id':
      return employee.fingerprint_id
        ? <span className="text-sm font-mono">{employee.fingerprint_id}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'face_id':
      return employee.face_id
        ? <span className="text-sm font-mono">{employee.face_id}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'card_id':
      return employee.card_id
        ? <span className="text-sm font-mono">{employee.card_id}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'supervisor_name':
      return employee.supervisor_name
        ? <span className="text-sm">{employee.supervisor_name}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'coach_name':
      return employee.coach_name
        ? <span className="text-sm">{employee.coach_name}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'mentor_name':
      return employee.mentor_name
        ? <span className="text-sm">{employee.mentor_name}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'is_postier':
      return employee.is_postier
        ? <Badge variant="outline" className="border-green-900/40 text-green-400 bg-green-950/20">{t('common.yes')}</Badge>
        : <span className="text-muted-foreground">{t('common.no')}</span>;

    case 'regime':
      return employee.regime
        ? <Badge variant="secondary">{employee.regime}</Badge>
        : <span className="text-muted-foreground">—</span>;

    case 'hire_date':
      return employee.hire_date
        ? <span className="text-sm">{formatDate(employee.hire_date)}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'contract_end_date':
      return employee.contract_end_date
        ? <span className="text-sm">{formatDate(employee.contract_end_date)}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'created_at':
      return employee.created_at
        ? <span className="text-sm">{formatDate(employee.created_at)}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'updated_at':
      return employee.updated_at
        ? <span className="text-sm">{formatDate(employee.updated_at)}</span>
        : <span className="text-muted-foreground">—</span>;

    case 'actions':
      return (
        <TooltipProvider>
          <div className="flex items-center justify-end space-x-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => window.open(`/portal/dashboard?preview_employee=${employee.id}`, '_blank', 'noopener')}
                  className="text-primary hover:text-primary hover:bg-primary/10"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t('employees.portalPreview')}</TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="icon" onClick={() => handleEdit(employee)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { setSelectedEmployee(employee); setShowDeleteDialog(true); }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TooltipProvider>
      );

    default:
      return <span className="text-muted-foreground">—</span>;
  }
}
